import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material';
import {Customer} from '../../model/Entities/Customer';
import {AresResponse} from '../../model/Entities/AresResponse';
import {AresService} from '../../services/ares.service';
import {SnackService} from '../../services/snack.service';
import {CustomerService} from '../../services/customer.service';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';

@Component({
    selector: 'customer-ares-comparison-dialog',
    templateUrl: './customer-ares-comparison.dialog.component.html',
    styleUrls: ['./customer-ares-comparison.dialog.component.scss']
})
export class CustomerAresComparisonDialogComponent implements OnInit {
    customer: Customer;
    aresData: AresResponse = null;
    loading = false;
    updating = false;

    constructor(public dialog: MatDialogRef<CustomerAresComparisonDialogComponent>,
                private aresService: AresService,
                private snackService: SnackService,
                private customerService: CustomerService,
                private http: HttpClient,
                @Inject(MAT_DIALOG_DATA) public data: any) {
        this.customer = data.customer;
    }

    ngOnInit(): void {
        this.loadAresData();
    }

    private loadAresData(): void {
        if (!this.customer || !this.customer.invoiceCompanyNumber) {
            this.snackService.showWarn('Zákazník nemá vyplněné IČ.');
            this.dialog.close();
            return;
        }

        this.loading = true;
        this.aresService.getByIco(this.customer.invoiceCompanyNumber).subscribe(
            (aresResponse: AresResponse) => {
                this.aresData = aresResponse;
                this.loading = false;
            },
            (error) => {
                this.loading = false;
                console.error('ARES error:', error);
                this.snackService.showWarn('Nepodařilo se načíst data z ARES.');
            }
        );
    }

    getValue(field: string, source: 'current' | 'ares'): any {
        if (source === 'current') {
            const value = this.customer[field];
            return value !== null && value !== undefined ? value : '';
        } else {
            if (!this.aresData) {
                return '';
            }
            const value = this.aresData[field];
            return value !== null && value !== undefined ? value : '';
        }
    }

    areDifferent(field: string): boolean {
        if (!this.aresData) {
            return false;
        }
        if (field === 'invoiceVatPayer') {
            const customerVatPayer = this.customer.invoiceVatPayer == true ? 'Ano' : 'Ne';
            const aresVatPayer = this.aresData.invoiceVatPayer == true ? 'Ano' : 'Ne';
            return customerVatPayer !== aresVatPayer;
        }
        if (field === 'invoiceZip') {
            const customerZip = this.customer.invoiceZip.replace(/\s/g, '');
            const aresZip = this.aresData.invoiceZip.replace(/\s/g, '');
            return customerZip !== aresZip;

        }
        const current = this.customer[field];
        const ares = this.aresData[field];
        
        if (typeof current === 'boolean' || typeof ares === 'boolean') {
            return current !== ares;
        }
        
        const currentStr = current !== null && current !== undefined ? String(current) : '';
        const aresStr = ares !== null && ares !== undefined ? String(ares) : '';
        return currentStr !== aresStr;
    }

    updateCustomer(): void {
        if (!this.aresData) {
            return;
        }
        this.updating = true;
        this.customer.name = this.aresData.name || this.customer.name;
        this.customer.invoiceName = this.aresData.name || this.customer.invoiceName;
        this.customer.invoiceVatNumber = this.aresData.invoiceVatNumber || this.customer.invoiceVatNumber;
        this.customer.invoiceVatPayer = this.aresData.invoiceVatPayer !== undefined ? this.aresData.invoiceVatPayer : this.customer.invoiceVatPayer;
        if (this.aresData.invoiceStreet && this.aresData.invoiceCity) {
            const addressQuery = `${this.aresData.invoiceStreet}, ${this.aresData.invoiceCity}, ${this.aresData.invoiceZip || ''}`;
            this.loadAndSelectAddress(addressQuery).then(() => {
                this.saveCustomer();
            }).catch(() => {
                this.customer.invoiceStreet = this.aresData.invoiceStreet || this.customer.invoiceStreet;
                this.customer.invoiceCity = this.aresData.invoiceCity || this.customer.invoiceCity;
                this.customer.invoiceZip = this.aresData.invoiceZip || this.customer.invoiceZip;
                this.saveCustomer();
            });
        } else {
            this.saveCustomer();
        }
    }

    private loadAndSelectAddress(search: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!search || search.length < 3) {
                reject();
                return;
            }

            const apiKey = environment.apiMapyCom;
            const url = `https://api.mapy.com/v1/suggest?query=${encodeURIComponent(search)}&type=regional.address,poi&apiKey=${apiKey}`;

            this.http.get<any>(url).subscribe(
                (response) => {
                    const addresses = response.items || [];
                    if (addresses.length > 0) {
                        this.selectAddress(addresses[0]);
                        resolve();
                    } else {
                        reject();
                    }
                },
                (error) => {
                    console.error("Nepodařilo se načíst adresy:", error);
                    reject();
                }
            );
        });
    }

    private selectAddress(address: any): void {
        this.customer.invoiceCity = '';
        this.customer.invoiceZip = '';
        this.customer.invoiceStreet = '';
        
        let hasMunicipalityPart = address.regionalStructure.some(s => s.type === 'regional.municipality_part');
        
        address.regionalStructure.forEach(structure => {
            if (structure.type === 'regional.municipality_part') {
                if (this.customer.invoiceCity !== '') {
                    this.customer.invoiceCity = structure.name + ' - ' + this.customer.invoiceCity;
                } else {
                    this.customer.invoiceCity = structure.name;
                }
            }
            if (structure.type === 'regional.municipality' && !hasMunicipalityPart) {
                this.customer.invoiceCity = structure.name;
            }
            if (structure.type === 'regional.street' || structure.type === 'regional.address') {
                this.customer.invoiceStreet = structure.name + ' ' + this.customer.invoiceStreet;
            }
        });
        
        this.customer.invoiceZip = address.zip;
        this.customer.address = address.name + ', ' + address.zip + ', ' + address.location;
        this.customer.addressLat = address.position.lat;
        this.customer.addressLon = address.position.lon;
    }

    private saveCustomer(): void {
        this.customerService.update(this.customer.id, this.customer).subscribe(
            (updatedCustomer: Customer) => {
                this.updating = false;
                this.snackService.showSuccess('Údaje zákazníka byly úspěšně aktualizovány z ARES.');
                this.dialog.close(updatedCustomer);
            },
            (error) => {
                this.updating = false;
                console.error('Chyba při aktualizaci zákazníka:', error);
                this.snackService.showWarn('Nepodařilo se aktualizovat údaje zákazníka.');
            }
        );
    }
}



// WEBPACK FOOTER //
// /app/src/CustomersModule/CustomerAresComparisonDialogComponent/customer-ares-comparison.dialog.component.ts